import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import { useSingleUser, useUpdateUser } from "@/hooks";
import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import { toast } from "sonner";
import { UserRole, UserStatus } from "@/interfaces/profile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";

const AdminUserEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: user, isLoading, isError, refetch } = useSingleUser(id!);

  const updateUser = useUpdateUser(id!);

  const [openVerify, setOpenVerify] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    merchantName: "",
    role: "" as UserRole | "",
    status: "" as UserStatus | "",
    // phoneVerified: false,
    emailVerified: false,
    twoFAEnabled: false,
  });

  useEffect(() => {
    if (!user) return;

    setForm({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      middleName: user.middleName ?? "",
      merchantName: user.merchantName ?? "",
      role: user.role ?? "",
      status: user.status ?? "",
      // phoneVerified: user.phoneVerified ?? false,
      emailVerified: user.emailVerified ?? false,
      twoFAEnabled: user.setting.twoFAEnabled ?? false,
    });
  }, [user]);

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  const handleChange = <K extends keyof typeof form>(
    key: K,
    value: (typeof form)[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleVerifiedUpdate = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    try {
      await updateUser.mutateAsync({
        firstName: form.firstName,
        lastName: form.lastName,
        middleName: form.middleName,
        merchantName: form.merchantName,
        role: form.role || undefined,
        status: form.status || undefined,
        // phoneVerified: form.phoneVerified,
        emailVerified: form.emailVerified,
        twoFAEnabled: form.twoFAEnabled,
        otp,
        securityCode,
      });

      setOpenVerify(false);

      toast.success("User updated successfully");

      await refetch();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || "Update failed",
      );
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate(-1)}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      <h1 className="text-xl font-semibold">Edit User</h1>

      <div className="border rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            <Label className="mb-1 text-gray-500">First Name</Label>

            <Input
              className="!h-12"
              value={form.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </div>

          {/* Last Name */}
          <div>
            <Label className="mb-1 text-gray-500">Last Name</Label>

            <Input
              className="!h-12"
              value={form.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />
          </div>

          {/* Middle Name */}
          <div>
            <Label className="mb-1 text-gray-500">Middle Name</Label>

            <Input
              className="!h-12"
              value={form.middleName}
              onChange={(e) => handleChange("middleName", e.target.value)}
            />
          </div>

          {/* Merchant Name */}
          <div>
            <Label className="mb-1 text-gray-500">Merchant Name</Label>

            <Input
              className="!h-12"
              value={form.merchantName}
              onChange={(e) => handleChange("merchantName", e.target.value)}
            />
          </div>

          {/* Role */}
          <div className="flex flex-col">
            <Label className="mb-1 text-gray-500">User Role</Label>

            <Select
              value={form.role}
              onValueChange={(value) => handleChange("role", value as UserRole)}
            >
              <SelectTrigger className="w-full !h-12">
                <SelectValue placeholder="User Role" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={UserRole.USER}>User</SelectItem>

                <SelectItem value={UserRole.ADMIN}>Admin</SelectItem>

                <SelectItem value={UserRole.DISPUTE_MODERATOR}>
                  Dispute Moderator
                </SelectItem>

                <SelectItem value={UserRole.COMPLIANCE}>
                  Compliance Officer
                </SelectItem>

                <SelectItem value={UserRole.SUPPORT}>Support Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="flex flex-col">
            <Label className="mb-1 text-gray-500">Account Status</Label>

            <Select
              value={form.status}
              onValueChange={(value) =>
                handleChange("status", value as UserStatus)
              }
            >
              <SelectTrigger className="w-full !h-12">
                <SelectValue placeholder="User Status" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={UserStatus.ACTIVE}>Activate</SelectItem>

                <SelectItem value={UserStatus.SUSPENDED}>Suspend</SelectItem>

                <SelectItem value={UserStatus.BANNED}>Ban</SelectItem>

                <SelectItem value={UserStatus.DELETED}>Delete</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Phone Verification */}
          {/* <div className="flex flex-col">
            <Label className="mb-1 text-gray-500">Phone Verification</Label>

            <Select
              value={form.phoneVerified ? "true" : "false"}
              onValueChange={(value) =>
                handleChange("phoneVerified", value === "true")
              }
            >
              <SelectTrigger className="w-full !h-12">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="true">Verified</SelectItem>

                <SelectItem value="false">Not Verified</SelectItem>
              </SelectContent>
            </Select>
          </div> */}

          {/* Email Verification */}
          <div className="flex flex-col">
            <Label className="mb-1 text-gray-500">Email Verification</Label>

            <Select
              value={form.emailVerified ? "true" : "false"}
              onValueChange={(value) =>
                handleChange("emailVerified", value === "true")
              }
            >
              <SelectTrigger className="w-full !h-12">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="true">Verified</SelectItem>

                <SelectItem value="false">Not Verified</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 2FA */}
          <div className="flex flex-col">
            <Label className="mb-1 text-gray-500">
              Two-Factor Authentication
            </Label>

            <Select
              value={form.twoFAEnabled ? "true" : "false"}
              onValueChange={(value) =>
                handleChange("twoFAEnabled", value === "true")
              }
            >
              <SelectTrigger className="w-full !h-12">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>

                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => setOpenVerify(true)}
          disabled={updateUser.isPending}
        >
          {updateUser.isPending ? "Updating..." : "Update User"}
        </Button>
      </div>

      <AdminVerificationDialog
        open={openVerify}
        onOpenChange={setOpenVerify}
        systemType="user"
        onSuccess={handleVerifiedUpdate}
      />
    </div>
  );
};

export default AdminUserEdit;
